import React, { FC } from "react";
import { Icon, IconTypesKeys } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import {
  LayerControl,
  LayerInformation,
  LayerItem,
  LayerTitle,
  LayerTitleText,
  ProjectControl,
  ProjectIconContainer,
  ProjectInformation,
  ProjectItem,
  ProjectSubtitle,
  ProjectTitle,
  ProjectTitleContainer,
  TableControl,
  TableIconContainer,
  TableInformation,
  TableItem,
  TableStructure,
  TableSubtitle,
  TableTitle,
  TableTitleContainer,
} from "./styled";

import { AccessControl } from "../components/AccessControl";
import { CommonSettingsControls, StructureWrapper } from "../styled";

import { isConfigure, isRead, isWrite } from "../../../../utils/permissions";

import { ProjectStructureProps } from "../types";

// eslint-disable-next-line max-lines-per-function
export const ProjectStructure: FC<ProjectStructureProps> = ({
  activeAclRole,
  structure,
  readOnly,
  onUpdateProject,
  onUpdateEntity,
}) => {
  const { t } = useTranslation("dialogs");
  const projectAcl = structure.acl.data.find(item => item.role === activeAclRole);
  const { permissions: projectPermissions } = projectAcl || {};

  return (
    <StructureWrapper>
      <ProjectItem>
        <ProjectInformation>
          <ProjectIconContainer>
            <Icon kind="map" />
          </ProjectIconContainer>
          <ProjectTitleContainer>
            <ProjectTitle>{structure.alias || structure.name}</ProjectTitle>
            <ProjectSubtitle>
              {t("access.owner")}: {structure.owner || " "}
            </ProjectSubtitle>
          </ProjectTitleContainer>
        </ProjectInformation>
        <ProjectControl>
          <CommonSettingsControls>
            <AccessControl
              type="read"
              active={isRead(projectPermissions)}
              clickable={!isRead(projectPermissions)}
              onClick={() => !isRead(projectPermissions) && onUpdateProject("read", projectPermissions)}
            />
            {!readOnly && (
              <>
                <AccessControl
                  type="write"
                  active={isWrite(projectPermissions)}
                  onClick={() => onUpdateProject("write", projectPermissions)}
                />
                <AccessControl
                  type="configure"
                  active={isConfigure(projectPermissions)}
                  onClick={() => onUpdateProject("configure", projectPermissions)}
                />
              </>
            )}
          </CommonSettingsControls>
        </ProjectControl>
      </ProjectItem>
      {structure.tables.map(table => {
        const tableAcl = table.acl.data.find(item => item.role === activeAclRole);
        const { permissions: tablePermissions } = tableAcl || {};
        const isTableDisabled = !isConfigure(table.permissions);

        return (
          <TableStructure key={table.name}>
            <TableItem>
              <TableInformation warning={isTableDisabled}>
                <TableIconContainer>
                  <Icon kind={`geom_${table.geometries}` as IconTypesKeys} />
                </TableIconContainer>
                <TableTitleContainer>
                  <TableTitle>{table.alias || table.name}</TableTitle>
                  <TableSubtitle>
                    {t("access.objects")}: {table.rowCount || 0}
                    <span style={{ display: "inline-block", width: "1rem" }} />
                    {t("access.owner")}: {table.owner || " "}
                  </TableSubtitle>
                </TableTitleContainer>
              </TableInformation>
              <TableControl>
                <CommonSettingsControls>
                  <AccessControl
                    type="read"
                    disabled={isTableDisabled}
                    active={isRead(tablePermissions)}
                    clickable={!isRead(tablePermissions)}
                    onClick={() =>
                      !isRead(tablePermissions) && onUpdateEntity(table.name, table.type, "read", tablePermissions)
                    }
                  />
                  {!readOnly && (
                    <>
                      <AccessControl
                        type="write"
                        disabled={isTableDisabled}
                        active={isWrite(tablePermissions)}
                        onClick={() => onUpdateEntity(table.name, table.type, "write", tablePermissions)}
                      />
                      <AccessControl
                        type="configure"
                        disabled={isTableDisabled}
                        active={isConfigure(tablePermissions)}
                        onClick={() => onUpdateEntity(table.name, table.type, "configure", tablePermissions)}
                      />
                    </>
                  )}
                </CommonSettingsControls>
              </TableControl>
            </TableItem>
            {table.layers.map(layer => {
              const layerAcl = layer.acl.data.find(item => item.role === activeAclRole);
              const { permissions: layerPermissions } = layerAcl || {};
              const isLayerDisabled = !isConfigure(layer.permissions);

              return (
                <LayerItem key={layer.name}>
                  <LayerInformation warning={isLayerDisabled}>
                    <LayerTitle>
                      <LayerTitleText>{layer.alias || layer.name}</LayerTitleText>
                    </LayerTitle>
                  </LayerInformation>
                  <LayerControl>
                    <CommonSettingsControls>
                      <AccessControl
                        type="read"
                        disabled={isLayerDisabled}
                        active={isRead(layerPermissions)}
                        clickable={!isRead(layerPermissions)}
                        onClick={() =>
                          !isRead(layerPermissions) && onUpdateEntity(layer.name, layer.type, "read", layerPermissions)
                        }
                      />
                      {!readOnly && (
                        <>
                          <AccessControl
                            type="write"
                            disabled={isLayerDisabled}
                            active={isWrite(layerPermissions)}
                            onClick={() => onUpdateEntity(layer.name, layer.type, "write", layerPermissions)}
                          />
                          <AccessControl
                            type="configure"
                            disabled={isLayerDisabled}
                            active={isConfigure(layerPermissions)}
                            onClick={() => onUpdateEntity(layer.name, layer.type, "configure", layerPermissions)}
                          />
                        </>
                      )}
                    </CommonSettingsControls>
                  </LayerControl>
                </LayerItem>
              );
            })}
          </TableStructure>
        );
      })}
    </StructureWrapper>
  );
};
